"use client";

import { GeistProvider } from "@geist-ui/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider as JotaiProvider } from "jotai";
import { SessionProvider, useSession } from "next-auth/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useState, type PropsWithChildren } from "react";
import * as Sentry from "@sentry/nextjs";
import ErrorFallback from "./ErrorFallback";
import { api, createClientOptions } from "~/utils/api";
import { cookieConsentGiven } from "./CookieConsentCard";
import PHSessionReplay from "./PHSessionReplay";
import { type Session } from "next-auth";

const canInitPostHog = () => {
  return (
    typeof window !== "undefined" &&
    ((process.env.NODE_ENV !== "test" &&
      process.env.NODE_ENV !== "development") ||
      process.env.DEV_RUN_POSTHOG)
  );
};

// Initialize PostHog only in non-test environments or with specific dev flag
if (canInitPostHog()) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || "", {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        color: false,
        date: false,
        "datetime-local": false,
        email: false,
        month: false,
        number: false,
        range: false,
        search: false,
        tel: false,
        text: false,
        time: false,
        url: false,
        week: false,
        textarea: false,
        select: false,
      },
    },
    persistence: cookieConsentGiven() ? "localStorage+cookie" : "memory",
    person_profiles: "identified_only",
    disable_session_recording: true,
    capture_pageview: false,
    loaded: (posthog) => {
      if (process.env.NODE_ENV === "development") posthog.debug(); // Enable debug in dev
      // Register the environment to be included in all events
      posthog.register({
        environment: process.env.NODE_ENV,
      });
    },
  });
}

const PostHogUserIdentification = () => {
  const { data: session } = useSession();

  useEffect(() => {
    if (session?.user?.id && canInitPostHog()) {
      // Identify the user in PostHog with their user ID
      posthog.identify(session.user.id, { email: session.user.email });
    }
  }, [session]);

  return null; // This component only handles side effects
};

export const Providers = ({
  children,
  session,
}: PropsWithChildren<{ session: Session | null }>) => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <Sentry.ErrorBoundary
      showDialog
      // eslint-disable-next-line @typescript-eslint/unbound-method
      fallback={({ error, resetError }) => {
        if (error instanceof Error) {
          return (
            <ErrorFallback error={error} resetErrorBoundary={resetError} />
          );
        }

        if (
          typeof error === "object" &&
          error !== null &&
          "toString" in error
        ) {
          return (
            <ErrorFallback
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              error={new Error(error.toString())}
              resetErrorBoundary={resetError}
            />
          );
        }

        return (
          <ErrorFallback
            error={new Error("Unknown Error")}
            resetErrorBoundary={resetError}
          />
        );
      }}
    >
      <PostHogProvider client={posthog}>
        <SessionProvider session={session}>
          <api.Provider client={trpcClient} queryClient={queryClient}>
            <JotaiProvider>
              <QueryClientProvider client={queryClient}>
                <GeistProvider>
                  {children}
                  <PHSessionReplay />
                  <PostHogUserIdentification />
                </GeistProvider>
              </QueryClientProvider>
            </JotaiProvider>
          </api.Provider>
        </SessionProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  );
};

// Helper

const trpcClient = api.createClient(createClientOptions);
